import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { Trans } from "@lingui/macro";
import { Wifi } from "@carbon/icons-react";
import { alpha, Avatar, Container, Link, Typography, useTheme } from "@mui/material";
import WifiNetworks from "../Wifi/WifiNetworks";
import { ErrorBoundary, GlobalHeader, postClose } from "@WahooFitness/wahoo-offline-mfe";
import usePairedDevice from "@/hooks/usePairedDevice";
import { useNavigate } from "react-router-dom";
import { useBCFirstRunState } from "@/hooks/useBCFirstRunState";
import { WSMBoltAppFirstRunState } from "@WahooFitness/wsm-native/dist/esm/types/bolt_cfg";
import { useSettingsRemoteConfig } from "@/hooks/useSettingsRemoteConfig";
import { WfIconNavigationCloseRegular } from "@WahooFitness/redesignr";

interface WifiOnboardingProps {
  appToken: string;
}

const WifiOnboarding: React.FC<WifiOnboardingProps> = ({ appToken }) => {
  const { palette } = useTheme();
  const navigate = useNavigate();
  const { getSettingsRemoteConfigString } = useSettingsRemoteConfig();
  const [connected, setConnected] = useState(false);
  const wifiStatusCallback = useCallback((connected: boolean) => {
    setConnected(connected);
  }, []);

  const wifiRequirementsUrl = useMemo(
    () => getSettingsRemoteConfigString("ELEMNT_WIFI_REQUIREMENTS_URL"),
    [getSettingsRemoteConfigString]
  );

  const { device, isDeviceConnected, deviceError, reloadDevice } = usePairedDevice(appToken);
  const computerName = useMemo(() => device?.productTypeName, [device]);

  const { setStateTransition } = useBCFirstRunState(appToken);
  useEffect(() => {
    setStateTransition({
      desiredState: WSMBoltAppFirstRunState.AppSetupWifi,
      allowedStates: [WSMBoltAppFirstRunState.Complete],
    });
  }, [setStateTransition]);

  const closeOnboarding = useCallback(() => {
    postClose("onboarding");
  }, []);

  return (
    <>
      <GlobalHeader
        primaryRightActionItem={{
          icon: <WfIconNavigationCloseRegular />,
          action: closeOnboarding,
        }}
        hideBackButton
      />
      {/* Fixed height (not minHeight) so the flex column below has a definite height to
          divide: the network list gets its own scroll region and the Continue button stays
          pinned to the bottom. wf-self-scrolling-page suppresses the shell's page-end
          spacer, which would otherwise add a second scroll region below the fixed page. */}
      <Container
        className="wf-self-scrolling-page"
        maxWidth="sm"
        disableGutters
        sx={{ height: "100%" }}
      >
        <ErrorBoundary isLoading={!isDeviceConnected} error={deviceError} onReload={reloadDevice}>
          <Box display="flex" flexDirection="column" width="100%" height="100%">
            <Box flex={1} overflow="auto" pt={2}>
              <Box width="100%" px={2} display="flex" flexDirection="column" alignItems="center">
                <Avatar
                  sx={{ width: "76px", height: "76px", bgcolor: alpha(palette.info.main, 0.1) }}
                >
                  <Wifi color={palette.info.main} size={56} />
                </Avatar>
                <Typography variant="prose-lg-bold" mt={2} px={2} textAlign="center">
                  <Trans>Connect {computerName} to Wi-Fi</Trans>
                </Typography>
                <Typography variant="prose-sm" mt={2} textAlign="center">
                  <Trans>
                    Stay connected to your Wi-Fi network for automatic updates and faster map
                    downloads.
                  </Trans>
                </Typography>
              </Box>
              <WifiNetworks appToken={appToken} wifiStatusCallback={wifiStatusCallback} />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              px={4}
              pt={2}
              pb="calc(var(--wf-bottom-safe-area, 0px) + 16px)"
            >
              {!connected && (
                <>
                  <Typography variant="prose-xs-medium" textAlign="center">
                    <Trans>Don&apos;t see your Wi-Fi?</Trans>
                  </Typography>
                  <Typography
                    variant="prose-xs-medium"
                    textAlign="center"
                    sx={{ pb: 1 }}
                    color={palette.info.main}
                  >
                    <Link href={wifiRequirementsUrl} target="_blank">
                      <Trans>Learn more about network requirements for {computerName}.</Trans>
                    </Link>
                  </Typography>
                </>
              )}
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!connected}
                onClick={() => navigate("../firmware")}
              >
                <Trans>Continue</Trans>
              </Button>
            </Box>
          </Box>
        </ErrorBoundary>
      </Container>
    </>
  );
};

export { WifiOnboarding };
